/**
 * @file: chatMessageFromEvent.ts
 *
 * Build a `RemoteChatMsg` from an incoming `message.new` WebSocket envelope
 * (a WS-delivered message is already server-confirmed). A system envelope
 * carries no actor; a user envelope carries the actor + `authorType`. The
 * message-level `authorType` is derived from that origin so system
 * notifications render via the dedicated system-message path rather than the
 * text fallback.
 */

import { ChatMessageNewEvent } from '@euler/app/flows/chat/lib/session/chatEvents';
import { RemoteChatMsg } from '@euler/model/chat';
import { IdType } from '@euler/model/common';

export function chatMessageFromEvent(
  event: ChatMessageNewEvent,
  roomId: string,
): RemoteChatMsg {
  const author =
    event.type === 'user'
      ? {
          authorId: event.platformUserId,
          authorType: event.authorType,
          authorName: event.authorName ?? null,
          authorAvatar: event.authorAvatar ?? null,
        }
      : {
          authorId: '' as IdType,
          authorType: 'system',
          authorName: null,
          authorAvatar: null,
        };
  return {
    kind: 'remote',
    messageId: event.body.messageId,
    // The delivery handler stamps the recipient-row id on the envelope; the
    // fallback only guards a malformed event (never the normal path).
    id: event.recipientId ?? '',
    roomId,
    ...author,
    payloadType: event.body.payload.type,
    payload: event.body.payload,
    quoteMessageId: event.body.quoteMessageId,
    quoteSnapshot: event.body.quoteSnapshot,
    createdAt: event.body.createdAt,
    editedAt: null,
    deletedAt: null,
  };
}
